/**
 * 对外错误码全表 —— 契约 §3.3。
 *
 * 出网边界(HTTP + SSE)只有点分式 `DOMAIN.REASON` 一种形态(契约 §3.2)；
 * 内网的 `SUP_*` / `ORDER_*` 前缀码前端**永远看不到**，不要为它们建映射。
 *
 * ⚠ 表外的码不是异常，是常态 —— 后端新增错误码不需要前端发版。
 *   一律落 {@link UNKNOWN_ERROR_MESSAGE_KEY} + 显示 traceId 兜底。
 */

/** 成功码。契约 §2：成功恒为 `OK`。⚠ 它不是点分式，别拿它去过错误码正则。 */
export const SUCCESS_CODE = 'OK'

/** AUTH(9)。契约 §3.3 要求前端**全部处理** —— 九条各有各的界面动作，见 M1。 */
export const AUTH_ERROR_CODES = [
  'AUTH.LOGIN_REQUIRED',
  'AUTH.TOKEN_EXPIRED',
  'AUTH.TOKEN_INVALID',
  'AUTH.CODE_INVALID',
  'AUTH.CODE_EXPIRED',
  'AUTH.CODE_TOO_FREQUENT',
  'AUTH.PASSWORD_INVALID',
  'AUTH.ACCOUNT_DISABLED',
  'AUTH.ACTION_FORBIDDEN',
] as const

/** COMMON(9，横切)。只有 RATE_LIMITED 与 UPSTREAM_UNAVAILABLE 是 `retryable=true`。 */
export const COMMON_ERROR_CODES = [
  'COMMON.INTERNAL',
  'COMMON.INVALID_ARGUMENT',
  'COMMON.FAILED_PRECONDITION',
  'COMMON.UNAUTHENTICATED',
  'COMMON.FORBIDDEN',
  'COMMON.NOT_FOUND',
  'COMMON.CONFLICT',
  'COMMON.RATE_LIMITED',
  'COMMON.UPSTREAM_UNAVAILABLE',
] as const

/**
 * IDENTITY(10)。D-1 出行人建成后才会出现，表先建好。全部 `retryable=false`。
 *
 * ⚠ **2026-09-18 第 10 个**：`IDENTITY.CREDENTIAL_FORMAT_INVALID`（打车账号绑定 M2.5）。
 *   它是**格式**错，不是「Token 不对」—— 后端录入时只校验格式（空 / 过长 / 含换行等
 *   不可见字符），Token 真伪要到第一次实际叫车时才由供应商判定（「懒发现」）。
 *   ⇒ 文案只能说「格式不对，请重新复制一次」，**不能**说「Token 无效」：
 *   用户刚从微信里复制的那一段，多半只是粘漏了一截。
 */
export const IDENTITY_ERROR_CODES = [
  'IDENTITY.USER_NOT_FOUND',
  'IDENTITY.EMAIL_EXISTS',
  'IDENTITY.VERSION_CONFLICT',
  'IDENTITY.DOC_INVALID',
  'IDENTITY.TRAVELER_NOT_FOUND',
  'IDENTITY.TRAVELER_FORBIDDEN',
  'IDENTITY.TRAVELER_ARCHIVED',
  'IDENTITY.VAULT_NOT_CONFIGURED',
  'IDENTITY.AUDIT_WRITE_FAILED',
  /** ⚠ 只说明格式不合（空/过长/含不可见字符），**不说明** Token 真伪。见上面头注。 */
  'IDENTITY.CREDENTIAL_FORMAT_INVALID',
] as const

/**
 * TRADE(6) —— 契约 §3.4，**2026-09-03 随 D-2 订单 REST 落地，现在真能到前端了**。
 *
 * 由 trade 出网边 `OutboundErrorMapper` + `TradeError` 把内网 `SUP_FLIGHT_*` 翻成点分码。
 * ⚠⚠ **原来那句「今天前端拿不到这个码，所以按 code 禁用重试按钮暂时做不到」已作废**
 *   —— D-2 落地后订单取消/详情链路上这六个码都会以点分式出现在 `Result.code` 里。
 *
 * 六个码**全部 `retryable=false`**，所以 {@link shouldRetry} 天然就不会重试它们。
 * 但其中一个要**在界面上单独说死**：
 *
 * ⚠⚠ **`TRADE.RESULT_UNKNOWN`：必须禁用重试按钮，不是「保守」是纪律。**
 *   供应商侧**可能已经成功了**，重发会造成重复 PNR / 重复扣我方预存款。
 *   不能因为「反正幂等」就放开。
 *
 * ⚠ 另外两个文案方向不能写绝对：
 *   - `TRADE.INVENTORY_GONE` 是「认不出的拒绝」的兜底，**不能断定一定是没座位** ——
 *     写「这班订不到了，换一班」，别写「已售罄」；
 *   - `TRADE.PRICE_CHANGED` **说不出新价是多少**（供应商变价直接返回错误、不回新价）——
 *     写「价格变了，请重新确认」，别写「涨价了」（我方并不知道方向）；
 *   - `TRADE.UNAVAILABLE`（我方预存款不足）多发生在**出票**阶段，那时**用户已付过钱** ——
 *     必须「暂时无法出票，请联系客服」，**绝不能**是「换一班」。
 */
export const TRADE_ERROR_CODES = [
  'TRADE.INVENTORY_GONE',
  'TRADE.PRICE_CHANGED',
  'TRADE.QUOTE_EXPIRED',
  'TRADE.TRAVELER_DOC_INVALID',
  'TRADE.RESULT_UNKNOWN',
  'TRADE.UNAVAILABLE',
] as const

/** ⚠ 收到它必须禁用重试入口 —— 供应商可能已成功，重发会重复扣款 / 重复出票。 */
export const TRADE_RESULT_UNKNOWN = 'TRADE.RESULT_UNKNOWN'

/** 认识的码全集 = 27 个全表 + 6 个 TRADE 码。 */
export const KNOWN_ERROR_CODES = [
  ...AUTH_ERROR_CODES,
  ...COMMON_ERROR_CODES,
  ...IDENTITY_ERROR_CODES,
  ...TRADE_ERROR_CODES,
] as const

export type AuthErrorCode = (typeof AUTH_ERROR_CODES)[number]
export type CommonErrorCode = (typeof COMMON_ERROR_CODES)[number]
export type IdentityErrorCode = (typeof IDENTITY_ERROR_CODES)[number]
export type TradeErrorCode = (typeof TRADE_ERROR_CODES)[number]
export type KnownErrorCode = (typeof KNOWN_ERROR_CODES)[number]

const KNOWN = new Set<string>(KNOWN_ERROR_CODES)

/** 出网码的形态。契约 §3.2：前端可以把 `code` 当成必然匹配这个正则的字符串。 */
export const ERROR_CODE_PATTERN = /^[A-Z][A-Z0-9]*\.[A-Z][A-Z0-9_]*$/

/** 是不是点分式。⚠ `OK` 不是，别把成功码喂进来。 */
export function isDottedErrorCode(code: string): boolean {
  return ERROR_CODE_PATTERN.test(code)
}

export function isKnownErrorCode(code: string): code is KnownErrorCode {
  return KNOWN.has(code)
}

/** 文案键的前缀。真正的文案在 i18n 资源里，本包只定键，不存中文。 */
export const ERROR_MESSAGE_KEY_PREFIX = 'error.'

/** 表外的码统一落这个键。⚠ 配套必须显示 traceId，否则用户报错时我们查不到链路。 */
export const UNKNOWN_ERROR_MESSAGE_KEY = 'error.unknown'

/**
 * code → i18n 文案键。
 *
 * ⚠ 后端 `message` 字段是**面向开发者**的(契约 §2)，不要直接展示给用户。
 */
export function messageKeyOf(code: string): string {
  return isKnownErrorCode(code) ? `${ERROR_MESSAGE_KEY_PREFIX}${code}` : UNKNOWN_ERROR_MESSAGE_KEY
}

/**
 * 要不要重试 —— **只看服务端给的 `retryable`**(契约 §2.1)。
 *
 * 前端**不维护「哪些码能重试」的清单**，这是后端 2026-09-01 的明确要求，理由也是对的：
 * 判据本来就在服务端(`ErrorCode.retryable()` 的注释写着「重试同一个请求有没有可能成功」)，
 * 而前端自己维护的那张表**漏一行在正常流量下看不出来** —— 只有上游真抖动时
 * 才暴露成一场重试风暴。
 *
 * ⚠ 残留风险要认：这等于把「不该重试」的判断完全托付给服务端那一位。
 *   若哪天它对 `TRADE.RESULT_UNKNOWN` 之类给了 `true`，前端不会再兜第二道 ——
 *   那是服务端的 bug，但代价(重复 PNR / 重复扣预存款)落在用户身上。
 *
 * ⚠ 另外两道闸门在 HTTP 层，与本函数无关：
 *   写操作没有 `x-idempotency-key` 一律不重试；退避有次数与上限。
 */
export function shouldRetry(retryable: boolean): boolean {
  return retryable
}
